/**
 * Concept Registry — keyword-driven intent classification.
 *
 * Replaces ~270 lines of dataset-specific regexes with a declarative
 * keyword-to-operation mapping. Each "concept" declares:
 *   - keywords: words/phrases that signal this dataset
 *   - opType: the operation name in the OperationRegistry
 *   - buildIntent(text, match): function that extracts parameters from the user text
 *
 * The registry is scanned in priority order. The first concept whose
 * keywords match the user text wins. This gives us:
 *   1. Auto-discovery — new datasets just need a concept entry
 *   2. Composability — concepts can be combined by the pipeline executor
 *   3. Maintainability — one place to add/edit dataset triggers
 *
 * Phase 2 of "Building a world-class NLQ intent parser for EarthVoice"
 */

// ── Location extractor (shared across all concepts) ────────────────
// Pulls "in Africa", "of Asia", "for the United States" etc. from text
const LOCATION_RE = /\s+(?:in|of|for|across|around)\s+(?:the\s+)?(.+?)$/i

function extractLocation(text) {
  const m = text.match(LOCATION_RE)
  return m ? m[1].trim() : null
}

// ── Concept definitions ─────────────────────────────────────────────
// Each concept: { id, keywords[], opType, priority, buildIntent(text) }
// Priority: lower number = checked first (for overlapping keywords)
//
// Keywords are matched case-insensitively against the full user text.
// A concept matches if ALL keywords in at least one keyword group match.
// Keyword groups are arrays within the keywords array:
//   keywords: [['wheat', 'production'], ['rice', 'harvest']]
//   means: (wheat AND production) OR (rice AND harvest)

const CONCEPTS = [
  // ── FAOSTAT Agriculture ──────────────────────────────────────────
  {
    id: 'faostat',
    opType: 'faostat',
    priority: 10,
    keywords: [
      ['wheat', 'production'],
      ['rice', 'production'],
      ['maize', 'production'],
      ['corn', 'production'],
      ['cereal', 'production'],
      ['grain', 'production'],
      ['crop', 'production'],
      ['food', 'production'],
      ['wheat', 'output'],
      ['rice', 'output'],
      ['cereal', 'output'],
      ['wheat', 'harvest'],
      ['rice', 'harvest'],
      ['crop', 'harvest'],
      ['fertilizer'],
      ['fertiliser'],
      ['irrigation'],
      ['irrigated'],
      ['cattle'],
      ['livestock'],
      ['sheep'],
      ['poultry'],
      ['chicken'],
      ['crop yield'],
      ['cereal yield'],
      ['arable land'],
      ['agricultural land'],
      ['farmland'],
      ['cropland'],
      ['agricultural export'],
      ['agricultural import'],
      ['ag export'],
      ['ag import'],
      ['agricultural trade'],
      ['faostat'],
      ['agricultural data'],
      ['agriculture data'],
      ['agriculture map'],
      ['agriculture index'],
      ['agriculture statistics'],
      ['global agriculture'],
      ['global food production'],
      ['global crop production'],
    ],
    buildIntent(text) {
      // Try to find the specific indicator
      const indicatorMatch = text.match(
        /\b(wheat|rice|maize|corn|cereal|grain|crop|food|fertilizer|fertiliser|irrigation|irrigated|cattle|livestock|sheep|poultry|chicken|crop\s+yields?|cereal\s+yields?|arable\s+land|agricultural\s+land|farmland|cropland)\b/i
      )
      return {
        type: 'faostat',
        indicator: indicatorMatch ? indicatorMatch[1].trim() : null,
        location: extractLocation(text),
        _fastQuery: true,
        _conceptMatch: 'faostat',
      }
    },
  },

  // ── USDA FAS (Production, Supply & Distribution) ─────────────────
  {
    id: 'usda_fas',
    opType: 'usda_fas',
    priority: 10,
    keywords: [
      ['usda'],
      ['usda fas'],
      ['soybean', 'production'],
      ['soy', 'production'],
      ['cotton', 'production'],
      ['coffee', 'production'],
      ['cocoa', 'production'],
      ['tea', 'production'],
      ['palm oil'],
      ['sugarcane'],
      ['sugar cane'],
      ['peanut'],
      ['groundnut'],
      ['sunflower', 'production'],
      ['rapeseed'],
      ['canola'],
      ['almond', 'production'],
      ['soybean meal'],
      ['barley', 'production'],
      ['sorghum', 'production'],
      ['oat', 'production'],
      ['millet', 'production'],
      ['soybean', 'export'],
      ['cotton', 'export'],
      ['coffee', 'export'],
      ['soybean', 'import'],
      ['cotton', 'import'],
      ['coffee', 'import'],
      ['us', 'wheat', 'export'],
      ['us', 'corn', 'export'],
      ['us', 'soybean', 'export'],
      ['united states', 'export'],
    ],
    buildIntent(text) {
      const commodityMatch = text.match(
        /\b(wheat|corn|maize|rice|soybeans?|soy|cotton|coffee|cocoa|tea|palm\s*oil|sugar(?:\s*cane)?|peanuts?|groundnuts?|sunflower|rapeseed|canola|almonds?|soybean\s+meal|barley|sorghum|oats?|millet)\b/i
      )
      const attrMatch = text.match(
        /\b(production|imports?|exports?|consumption|stocks?|yields?|area|supply|distribution)\b/i
      )
      const usExport =
        /\b(?:us|u\.?s\.?|united\s+states?|american)\b/i.test(text) && /\bexport/i.test(text)
      return {
        type: 'usda_fas',
        commodity: commodityMatch ? commodityMatch[1].trim() : null,
        attribute: attrMatch ? attrMatch[1].trim() : 'production',
        us_exports: usExport || undefined,
        location: extractLocation(text),
        _fastQuery: true,
        _conceptMatch: 'usda_fas',
      }
    },
  },

  // ── Global Food Security Index ───────────────────────────────────
  {
    id: 'food_security',
    opType: 'food_security',
    priority: 10,
    keywords: [
      ['food security'],
      ['food insecurity'],
      ['gfsi'],
      ['food affordability'],
      ['food availability'],
      ['food quality'],
      ['food safety'],
      ['food sustainability'],
      ['food nutrition'],
      ['hunger index'],
      ['hunger map'],
    ],
    buildIntent(text) {
      const indicatorMatch = text.match(
        /\b(affordability|availability|quality|safety|sustainability|nutrition)\b/i
      )
      return {
        type: 'food_security',
        indicator: indicatorMatch ? indicatorMatch[1].trim() : 'overall',
        location: extractLocation(text),
        _fastQuery: true,
        _conceptMatch: 'food_security',
      }
    },
  },

  // ── UN Treaty Database ───────────────────────────────────────────
  {
    id: 'un_treaties',
    opType: 'un_treaties',
    priority: 10,
    keywords: [
      ['paris agreement'],
      ['kyoto protocol'],
      ['geneva convention'],
      ['npt'],
      ['unclos'],
      ['rome statute'],
      ['icc'],
      ['chemical weapons'],
      ['landmine'],
      ['cluster munition'],
      ['arms trade'],
      ['nuclear ban'],
      ['nuclear weapons ban'],
      ['nuclear proliferation'],
      ['tpnw'],
      ['montreal protocol'],
      ['biodiversity convention'],
      ['tobacco convention'],
      ['tobacco control'],
      ['refugee convention'],
      ['child rights'],
      ['children rights'],
      ['crc'],
      ['cedaw'],
      ['torture convention'],
      ['ottawa treaty'],
      ['un treaty'],
      ['un treaties'],
      ['treaty participation'],
      ['treaty map'],
      ['international law'],
      ['international agreement'],
      ['international treaty'],
      ['international convention'],
      ['nato members'],
      ['un members'],
      ['signed', 'agreement'],
      ['ratified', 'protocol'],
    ],
    buildIntent(text) {
      const treatyMatch = text.match(
        /\b(paris\s+agreement|kyoto\s+protocol|geneva\s+convention|npt|unclos|rome\s+statute|icc|chemical\s+weapons|landmine|cluster\s+munitions?|arms\s+trade|nuclear\s+(?:ban|weapons?\s+(?:ban|prohibition)|(?:non-?\s*)?proliferation)|tpnw|montreal\s+protocol|biodiversity|tobacco|refugee|child(?:ren)?(?:'?s?)?\s+rights?|crc|cedaw|torture|ottawa)\b/i
      )
      return {
        type: 'un_treaties',
        treaty: treatyMatch ? treatyMatch[1].trim() : null,
        location: extractLocation(text),
        _fastQuery: true,
        _conceptMatch: 'un_treaties',
      }
    },
  },

  // ── Fragile States Index ─────────────────────────────────────────
  {
    id: 'fragile_states',
    opType: 'fragile_states',
    priority: 10,
    keywords: [
      ['fragile states'],
      ['fragile state index'],
      ['fsi'],
      ['state fragility'],
      ['fragility index'],
      ['fragility'],
      ['fragile countries'],
      ['fragile nations'],
      ['alert', 'countries'],
      ['warning', 'countries'],
      ['stable', 'countries'],
      ['sustainable', 'countries'],
      ['most fragile'],
    ],
    buildIntent(text) {
      const categoryMatch = text.match(/\b(alert|warning|stable|sustainable)\b/i)
      return {
        type: 'fragile_states',
        category_filter: categoryMatch ? categoryMatch[1].trim() : undefined,
        location: extractLocation(text),
        _fastQuery: true,
        _conceptMatch: 'fragile_states',
      }
    },
  },

  // ── UNESCO World Heritage Sites ──────────────────────────────────
  {
    id: 'heritage_sites',
    opType: 'heritage_sites',
    priority: 10,
    keywords: [
      ['heritage site'],
      ['heritage sites'],
      ['unesco site'],
      ['unesco sites'],
      ['world heritage'],
      ['unesco heritage'],
      ['endangered heritage'],
      ['cultural heritage site'],
      ['natural heritage site'],
      ['mixed heritage site'],
    ],
    buildIntent(text) {
      const endangered = /endangered/i.test(text)
      const categoryMatch = text.match(/\b(cultural|natural|mixed)\b/i)
      const countQuery = /how\s+many/i.test(text)
      return {
        type: 'heritage_sites',
        location: extractLocation(text),
        endangered: endangered || undefined,
        category_filter: categoryMatch ? categoryMatch[1].trim() : undefined,
        count_query: countQuery || undefined,
        _fastQuery: true,
        _conceptMatch: 'heritage_sites',
      }
    },
  },

  // ── World Languages (Glottolog) ──────────────────────────────────
  {
    id: 'languages',
    opType: 'languages',
    priority: 15,
    keywords: [
      ['language', 'world'],
      ['world language'],
      ['language map'],
      ['language diversity'],
      ['linguistic diversity'],
      ['glottolog'],
      ['languages spoken'],
      ['languages in'],
      ['endangered language'],
      ['extinct language'],
      ['vulnerable language'],
      ['dying language'],
      ['threatened language'],
      ['indo-european language'],
      ['sino-tibetan language'],
      ['niger-congo language'],
      ['austronesian language'],
      ['afro-asiatic language'],
      ['dravidian language'],
      ['turkic language'],
      ['uralic language'],
      ['romance language'],
      ['germanic language'],
      ['slavic language'],
      ['semitic language'],
      ['polynesian language'],
      ['spread of', 'language'],
      ['where', 'spoken'],
      ['language data'],
      ['language list'],
    ],
    buildIntent(text) {
      const endangered = /\b(?:endangered|extinct|vulnerable|dying|threatened)\b/i.test(text)
      const familyMatch = text.match(
        /\b(indo[- ]european|sino[- ]tibetan|niger[- ]congo|austronesian|afro[- ]asiatic|dravidian|turkic|uralic|tai[- ]kadai|austroasiatic|austro[- ]asiatic|japonic|koreanic|mongolic|quechuan|bantu|celtic|romance|germanic|slavic|semitic|polynesian|papuan|aboriginal)\b/i
      )
      const countQuery = /how\s+many/i.test(text)
      const langSearch =
        text.match(/(?:spread|distribution|extent|range)\s+of\s+(.+)/i) ||
        text.match(/where\s+(.+?)\s+is\s+spoken/i)
      return {
        type: 'languages',
        location: extractLocation(text),
        endangered: endangered || undefined,
        family_filter: familyMatch ? familyMatch[1].trim() : undefined,
        count_query: countQuery || undefined,
        language_search: langSearch ? langSearch[1].trim() : undefined,
        _fastQuery: true,
        _conceptMatch: 'languages',
      }
    },
  },

  // ── World Religions & Ethnicity ──────────────────────────────────
  {
    id: 'religions',
    opType: 'religions',
    priority: 15,
    keywords: [
      ['religion', 'world'],
      ['world religion'],
      ['religion map'],
      ['dominant religion'],
      ['religious diversity'],
      ['ethnic diversity'],
      ['christian countries'],
      ['muslim countries'],
      ['islamic countries'],
      ['hindu countries'],
      ['buddhist countries'],
      ['jewish countries'],
      ['secular countries'],
      ['unaffiliated countries'],
      ['christianity', 'spread'],
      ['islam', 'spread'],
      ['christianity', 'dominant'],
      ['islam', 'dominant'],
      ['hinduism', 'dominant'],
      ['buddhism', 'dominant'],
      ['catholic'],
      ['protestant'],
      ['orthodox'],
      ['evangelical'],
      ['sunni'],
      ['shia'],
      ['shiite'],
      // CL-144: -ism forms — the word-boundary keyword matcher no longer
      // substring-matches "sikh" inside "Sikhism".
      ['sikhism'],
      ['jainism'],
      ['hinduism'],
      ['buddhism'],
      ['judaism'],
      ['protestantism'],
      ['catholicism'],
      ['theravada'],
      ['mahayana'],
      ['vajrayana'],
      ['sikh'],
      ['jain'],
      ['bahai'],
      ["baha'i"],
      ['shinto'],
      ['taoist'],
      ['taoism'],
      ['confucian'],
      ['confucianism'],
      ['zoroastrian'],
      ['zoroastrianism'],
      ['state religion'],
      ['official religion'],
      ['secular state'],
      ['islamic state'],
      ['muslim state'],
      ['theocracy'],
      ['muslim population'],
      ['christian population'],
      ['hindu population'],
      ['buddhist population'],
      ['sikh population'],
      ['religion trend'],
      ['religion projection'],
      ['religion 2050'],
      ['religious composition'],
      ['religion forecast'],
      ['religion data'],
      ['religion distribution'],
      ['religious map'],
      ['government religion'],
    ],
    buildIntent(text) {
      // CL-144: "<denomination> regions" asks for a regional (admin-level)
      // breakdown the country-level religions dataset can't answer — defer.
      if (/\bregions?\s*\??$/i.test(text)) return null
      const diversityMatch = text.match(/\b(religious|ethnic)\s+diversity/i)
      if (diversityMatch) {
        return {
          type: 'religions',
          diversity_mode: diversityMatch[1].trim(),
          location: extractLocation(text),
          _fastQuery: true,
          _conceptMatch: 'religions',
        }
      }

      const stateReligion =
        /\b(?:state|official|established)\s+religion/i.test(text) ||
        /\bsecular\s+(?:state|countries|nations)/i.test(text) ||
        /\b(?:islamic|muslim)\s+(?:state|theocrac)/i.test(text) ||
        /\bgovernment[\s-]+religion/i.test(text)
      if (stateReligion) {
        const filter = /\bsecular/i.test(text)
          ? 'Secular'
          : /\b(?:islamic|muslim)/i.test(text)
            ? 'Islam'
            : undefined
        return {
          type: 'religions',
          state_religion_mode: true,
          state_rel_filter: filter,
          location: extractLocation(text),
          _fastQuery: true,
          _conceptMatch: 'religions',
        }
      }

      const projectionMatch = text.match(/\b(christianity|islam)\b/i)
      if (projectionMatch && /\b(?:spread|trend|project|future|2050|grow|declin)/i.test(text)) {
        return {
          type: 'religions',
          projection_mode: projectionMatch[1].toLowerCase(),
          _fastQuery: true,
          _conceptMatch: 'religions',
        }
      }

      const popMap = {
        christian: 'christianity',
        christians: 'christianity',
        muslim: 'islam',
        muslims: 'islam',
        hindu: 'hinduism',
        hindus: 'hinduism',
        buddhist: 'buddhism',
        buddhists: 'buddhism',
        sikh: 'sikhism',
        sikhs: 'sikhism',
        jain: 'jainism',
        jains: 'jainism',
      }
      const popMatch =
        text.match(
          /\b(christian|christians|muslim|muslims|hindu|hindus|buddhist|buddhists|sikh|sikhs|jain|jains)\s+population\b/i
        ) || text.match(/how\s+many\s+(christians?|muslims?|hindus?|buddhists?|sikhs?|jains?)/i)
      if (popMatch) {
        const key = popMatch[1].toLowerCase()
        return {
          type: 'religions',
          population_mode: popMap[key] || key,
          location: extractLocation(text),
          _fastQuery: true,
          _conceptMatch: 'religions',
        }
      }

      const denomMatch = text.match(
        /\b(catholic|protestant|orthodox|evangelical|sunni|shia|shiite|theravada|mahayana|vajrayana|sikh|jain|bahai|baha'i|shinto|tao(?:ist|ism)?|confucian(?:ism)?|zoroastrian(?:ism)?)\b/i
      )
      const religionFilter = text.match(
        /\b(christian|muslim|islamic|hindu|buddhist|jewish|secular|unaffiliated)\b/i
      )

      return {
        type: 'religions',
        denomination: denomMatch ? denomMatch[1].trim().toLowerCase() : undefined,
        religion_filter: religionFilter ? religionFilter[1].trim() : undefined,
        location: extractLocation(text),
        _fastQuery: true,
        _conceptMatch: 'religions',
      }
    },
  },

  // ── Joshua Project — Ethnic & People Groups ─────────────────────
  {
    id: 'joshua_project',
    opType: 'joshua_project',
    priority: 15,
    keywords: [
      ['people group'],
      ['people groups'],
      ['ethnic group'],
      ['ethnic groups'],
      ['ethnolinguistic'],
      ['ethno-linguistic'],
      ['groups of people'],
      ['tribes of'],
      ['unreached people'],
      ['unreached peoples'],
      ['indigenous people'],
      ['indigenous peoples'],
      ['joshua project'],
      ['people group', 'map'],
      ['ethnic group', 'map'],
      ['people group', 'data'],
      ['ethnic group', 'data'],
      ['10/40 window'],
      ['10 40 window'],
      ['different peoples'],
      ['different people groups'],
      ['speaking', 'people group'],
      ['speaking', 'ethnic group'],
    ],
    buildIntent(text) {
      const countQuery = /how\s+many/i.test(text)
      const unreached = /\bunreached\b/i.test(text)
      const indigenous = /\bindigenous\b/i.test(text)
      const langFilter =
        text.match(/(?:speak(?:ing)?)\s+(.+)/i) ||
        text.match(/(.+?)[\s-]speaking\s+(?:people|ethnic)/i)
      const window1040 = /10[\s/]?40\s+window/i.test(text)
      return {
        type: 'joshua_project',
        location: window1040 ? '10/40 window' : extractLocation(text),
        count_query: countQuery || undefined,
        unreached: unreached || undefined,
        indigenous: indigenous || undefined,
        language_filter: langFilter ? langFilter[1].trim() : undefined,
        _fastQuery: true,
        _conceptMatch: 'joshua_project',
      }
    },
  },

  // ── WHO Global Health Observatory ────────────────────────────────
  {
    id: 'who_health',
    opType: 'who_health',
    priority: 10,
    keywords: [
      ['malaria'],
      ['hiv'],
      ['aids'],
      ['tuberculosis'],
      ['tb'],
      ['vaccination'],
      ['vaccine'],
      ['immunization'],
      ['dtp3'],
      ['dtp'],
      ['measles vaccine'],
      ['measles vaccination'],
      ['maternal mortality'],
      ['child mortality'],
      ['under-5 mortality'],
      ['under5 mortality'],
      ['infant mortality'],
      ['ncd mortality'],
      ['life expectancy'],
      ['doctors', 'country'],
      ['physicians', 'country'],
      ['nurses', 'country'],
      ['health workers'],
      ['fewest doctors'],
      ['most doctors'],
      ['obesity'],
      ['alcohol consumption'],
      ['tobacco', 'rate'],
      ['smoking'],
      ['clean water'],
      ['safe water'],
      ['drinking water'],
      ['water access'],
      ['sanitation'],
      ['wash'],
      ['health spending'],
      ['healthcare spending'],
      ['health expenditure'],
      ['stunting'],
      ['wasting'],
      ['malnutrition'],
      ['nutrition', 'rate'],
      ['who health'],
      ['global health indicator'],
      ['global health data'],
      ['global health statistics'],
    ],
    buildIntent(text) {
      const lower = text.toLowerCase()
      let indicator = 'life expectancy' // default

      if (/malaria/i.test(text)) indicator = 'malaria'
      else if (/\b(?:hiv|aids)\b/i.test(text)) indicator = 'hiv'
      else if (/\b(?:tuberculosis|tb)\b/i.test(text)) indicator = 'tuberculosis'
      else if (/measles/i.test(text)) indicator = 'measles'
      else if (/\b(?:vaccination|vaccine|immunization|dtp)/i.test(text)) indicator = 'vaccination'
      else if (/maternal\s+mortality/i.test(text)) indicator = 'maternal mortality'
      else if (/\b(?:ncd)\s+mortality/i.test(text)) indicator = 'ncd mortality'
      else if (/\b(?:child|under.?5|infant)\s+mortality/i.test(text)) indicator = 'under5_mortality'
      else if (/life\s+expectancy/i.test(text)) indicator = 'life expectancy'
      else if (/\bnurse/i.test(text)) indicator = 'nurses'
      else if (/\b(?:doctor|physician)/i.test(text)) indicator = 'doctors'
      else if (/\b(?:alcohol)/i.test(text)) indicator = 'alcohol'
      else if (/\b(?:tobacco|smoking)/i.test(text)) indicator = 'smoking'
      else if (/obesity/i.test(text)) indicator = 'obesity'
      else if (/sanitation/i.test(text)) indicator = 'sanitation'
      else if (/\b(?:clean\s+water|safe\s+water|drinking\s+water|water\s+access)/i.test(text))
        indicator = 'clean water'
      else if (/health\s+(?:spending|expenditure)/i.test(text)) indicator = 'health spending'
      else if (/wasting/i.test(text)) indicator = 'wasting'
      else if (/\b(?:stunting|malnutrition|nutrition)/i.test(text)) indicator = 'stunting'

      return {
        type: 'who_health',
        indicator,
        location: extractLocation(text),
        _fastQuery: true,
        _conceptMatch: 'who_health',
      }
    },
  },

  // ── FAO Aquastat — Water Resources ──────────────────────────────
  {
    id: 'aquastat',
    opType: 'aquastat',
    priority: 10,
    keywords: [
      ['water stress'],
      ['water scarcity'],
      ['water crisis'],
      ['water use', 'country'],
      ['water usage'],
      ['water withdrawal'],
      ['water consumption'],
      ['irrigation', 'country'],
      ['irrigated land'],
      ['freshwater availability'],
      ['renewable water'],
      ['water resources'],
      ['freshwater per capita'],
      ['agricultural water'],
      ['industrial water'],
      ['domestic water'],
      ['household water'],
      ['aquastat'],
      ['water resources data'],
      ['water resources map'],
    ],
    buildIntent(text) {
      let indicator = 'water stress' // default
      if (
        /\b(?:withdrawal|use|usage|consumption)\b/i.test(text) &&
        !/\b(?:agricultural|industrial|domestic|household)\b/i.test(text)
      ) {
        indicator = 'water withdrawal'
      } else if (/\b(?:irrigation|irrigated)\b/i.test(text)) {
        indicator = 'irrigation'
      } else if (/\b(?:freshwater|renewable\s+water|water\s+resources?)\b/i.test(text)) {
        indicator = 'freshwater per capita'
      } else if (/\bagricultural\b/i.test(text)) {
        indicator = 'agricultural water'
      } else if (/\bindustrial\b/i.test(text)) {
        indicator = 'industrial water'
      } else if (/\b(?:domestic|household)\b/i.test(text)) {
        indicator = 'domestic water'
      }
      return {
        type: 'aquastat',
        indicator,
        location: extractLocation(text),
        _fastQuery: true,
        _conceptMatch: 'aquastat',
      }
    },
  },

  // ── WTO Trade Statistics ─────────────────────────────────────────
  {
    id: 'wto_trade',
    opType: 'wto_trade',
    priority: 10,
    keywords: [
      ['export', 'country'],
      ['import', 'country'],
      ['global export'],
      ['global import'],
      ['merchandise export'],
      ['merchandise import'],
      ['export volume'],
      ['export data'],
      ['export map'],
      ['import volume'],
      ['import data'],
      ['import map'],
      ['trade balance'],
      ['trade deficit'],
      ['trade surplus'],
      ['trade openness'],
      ['trade', 'percent', 'gdp'],
      ['trade to gdp'],
      ['trade flow'],
      ['trade partner'],
      ['trade with'],
      ['trade from'],
      ['high-tech export'],
      ['high tech export'],
      ['technology export'],
      ['service export'],
      ['services trade'],
      ['current account'],
      ['current account balance'],
      ['wto trade'],
      ['trade statistics'],
      ['trade data'],
      ['trade map'],
      // CL-144: World Bank-style indicator phrasings
      ['exports of goods'],
      ['imports of goods'],
    ],
    buildIntent(text) {
      let indicator = 'exports' // default
      if (/\bimport/i.test(text)) indicator = 'imports'
      else if (/trade\s+(?:balance|deficit|surplus)/i.test(text)) indicator = 'trade balance'
      else if (
        /trade\s+(?:openness|(?:as\s+)?percent)/i.test(text) ||
        /trade\s+to\s+gdp/i.test(text)
      )
        indicator = 'trade openness'
      else if (/\b(?:high[\s-]?tech|technology)\b/i.test(text)) indicator = 'high tech'
      else if (/\bservice/i.test(text)) indicator = 'service exports'
      else if (/current\s+account/i.test(text)) indicator = 'current account'

      const compareMatch = text.match(
        /compare\s+(?:exports?|imports?|trade)\s*(?::|\s+)\s*(.+?)\s+(?:vs?\.?|versus|and)\s+(.+)/i
      )
      const partnerMatch = text.match(
        /(?:trade\s+(?:flows?|partners?|with|from|data)|what\s+does?\s+.+?\s+(?:export|import|trade))\s*(?:from|with|of|for)\s+(.+)/i
      )

      return {
        type: 'wto_trade',
        indicator,
        location: extractLocation(text),
        partner: partnerMatch ? partnerMatch[1].trim() : undefined,
        compare: compareMatch ? [compareMatch[1].trim(), compareMatch[2].trim()] : undefined,
        _fastQuery: true,
        _conceptMatch: 'wto_trade',
      }
    },
  },

  // ── Natural Earth Physical Features ──────────────────────────────
  {
    id: 'physical_feature',
    opType: 'physical_feature',
    priority: 10,
    keywords: [
      ['mountain range'],
      ['mountain'],
      ['desert'],
      ['glacier'],
      ['ice sheet'],
      ['ice cap'],
      ['glaciated'],
      ['coral reef'],
      ['reef'],
      ['major lake'],
      ['great lake'],
      ['physical feature'],
      ['landform'],
      // Specific features
      ['himalayas'],
      ['andes'],
      ['alps'],
      ['rockies'],
      ['rocky mountains'],
      ['appalachian'],
      ['urals'],
      ['atlas'],
      ['pyrenees'],
      ['caucasus'],
      ['karakoram'],
      ['sierra nevada'],
      ['sahara'],
      ['gobi'],
      ['kalahari'],
      ['mojave'],
      ['sonoran'],
      ['atacama'],
      ['arabian'],
      ['thar'],
      ['namib'],
      ['karakum'],
      ['simpson'],
      ['gibson'],
    ],
    buildIntent(text) {
      let feature = 'mountains' // default
      if (
        /desert/i.test(text) ||
        /\b(?:sahara|gobi|kalahari|mojave|sonoran|atacama|arabian|thar|namib|karakum|simpson|gibson)\b/i.test(
          text
        )
      ) {
        feature = 'deserts'
      } else if (/\b(?:glacier|ice\s+sheet|ice\s+cap|glaciated)\b/i.test(text)) {
        feature = 'glaciated'
      } else if (/\breef/i.test(text)) {
        feature = 'reefs'
      } else if (/\blake/i.test(text)) {
        feature = 'lakes'
      }

      const nameMatch = text.match(
        /\b(himalayas?|andes|alps|rockies|rocky\s+mountains?|appalachian|urals?|atlas|pyrenees|caucasus|karakoram|sierra\s+nevada|sahara|gobi|kalahari|mojave|sonoran|atacama|arabian|thar|namib|karakum|simpson|gibson)\b/i
      )

      return {
        type: 'physical_feature',
        feature,
        name: nameMatch ? nameMatch[1].trim() : undefined,
        location: nameMatch ? nameMatch[1].trim() : extractLocation(text),
        _fastQuery: true,
        _conceptMatch: 'physical_feature',
      }
    },
  },

  // ── Volcanoes (Smithsonian GVP) ──────────────────────────────────
  {
    id: 'volcanoes',
    opType: 'volcanoes',
    priority: 10,
    keywords: [
      ['volcano'],
      ['volcanoes'],
      ['volcanic'],
      ['active volcano'],
      ['stratovolcano'],
      ['shield volcano'],
      ['caldera'],
      ['cinder cone'],
      ['submarine volcano'],
      ['lava dome'],
      ['fissure volcano'],
      ['ring of fire'],
      ['erupted'],
      ['eruption'],
      ['volcanic hazard'],
      // Specific volcanoes
      ['vesuvius'],
      ['krakatoa'],
      ['pinatubo'],
      ['fuji'],
      ['etna'],
      ['helens'],
      ['tambora'],
      ['kilauea'],
      ['mauna loa'],
      ['stromboli'],
      ['popocatepetl'],
      ['cotopaxi'],
      ['eyjafjallaj'],
      ['rainier'],
      ['kilimanjaro'],
      ['erebus'],
    ],
    buildIntent(text) {
      // CL-144: "volcanic regions/zones" is a physical-geography region query
      // (golden pc-533 expects physical_features) — defer to the AI rather
      // than rendering the point dataset.
      if (/\bvolcanic\s+(?:regions?|zones?|areas?)\b/i.test(text)) return null
      const activeOnly = /\b(?:active|historically?\s+active)\b/i.test(text)
      const typeMatch = text.match(
        /\b(stratovolcanoes?|shield\s+volcanoes?|calderas?|cinder\s+cones?|submarine\s+volcanoes?|lava\s+domes?|fissure\s+volcanoes?)\b/i
      )
      const nameMatch = text.match(
        /\b(?:mount\s+|mt\.?\s+)?(vesuvius|krakatoa|pinatubo|fuji|etna|st\.?\s*helens?|tambora|kilauea|mauna\s+loa|stromboli|popocatepetl|cotopaxi|eyjafjallaj[oö]kull|rainier|kilimanjaro|erebus)\b/i
      )
      const recentMatch = text.match(/(?:last|past)\s+(\d+)\s+years?/i)
      const ringOfFire = /ring\s+of\s+fire/i.test(text)

      return {
        type: 'volcanoes',
        active_only: activeOnly || undefined,
        volcano_type: typeMatch ? typeMatch[1].trim() : undefined,
        name: nameMatch ? nameMatch[1].trim() : undefined,
        recent_years: recentMatch
          ? parseInt(recentMatch[1])
          : /\b(?:recent|latest)\s+(?:volcanic\s+)?eruption/i.test(text)
            ? 25
            : undefined,
        location: ringOfFire ? 'Ring of Fire' : extractLocation(text),
        _fastQuery: true,
        _conceptMatch: 'volcanoes',
      }
    },
  },

  // ── NOAA Climate Data ────────────────────────────────────────────
  {
    id: 'noaa_climate',
    opType: 'noaa_climate',
    priority: 10,
    keywords: [
      ['ocean current'],
      ['sea current'],
      ['warm current'],
      ['cold current'],
      ['warmest ocean'],
      ['coldest ocean'],
      ['hottest ocean'],
      ['freezing ocean'],
      ['el nino'],
      ['el niño'],
      ['la nina'],
      ['la niña'],
      ['enso'],
      ['gulf stream'],
      ['kuroshio'],
      ['humboldt'],
      ['benguela'],
      ['labrador current'],
      ['agulhas'],
      ['california current'],
      ['canary current'],
      ['somali current'],
      ['antarctic circumpolar'],
      ['north atlantic drift'],
      ['brazil current'],
      ['east australian'],
      ['oyashio'],
      ['alaska current'],
      ['thermohaline'],
      ['ocean circulation'],
      ['deep ocean circulation'],
    ],
    buildIntent(text) {
      let subCommand = 'currents' // default
      if (/\b(?:el\s*ni[ñn]o|la\s*ni[ñn]a|enso)\b/i.test(text)) {
        subCommand = 'el_nino'
      } else if (/\b(?:warmest|hottest)\b/i.test(text)) {
        subCommand = 'warmest'
      } else if (/\b(?:coldest|freezing)\b/i.test(text)) {
        subCommand = 'coldest'
      }

      const currentType = text.match(/\b(warm|cold)\s+(?:ocean\s+)?current/i)
      const namedCurrent = text.match(
        /\b(gulf\s+stream|kuroshio|humboldt|benguela|labrador|agulhas|california|canary|somali|antarctic\s+circumpolar|north\s+atlantic\s+drift|brazil|east\s+australian|oyashio|alaska)\b/i
      )

      return {
        type: 'noaa_climate',
        sub_command: subCommand,
        current_type: currentType ? currentType[1].toLowerCase() : undefined,
        location: namedCurrent ? namedCurrent[1].trim() : extractLocation(text),
        _fastQuery: true,
        _conceptMatch: 'noaa_climate',
      }
    },
  },

  // ── Köppen Climate Zones ─────────────────────────────────────────
  {
    id: 'climate_zones',
    opType: 'climate_zones',
    priority: 10,
    keywords: [
      ['climate zone'],
      ['climate classification'],
      ['climate type'],
      ['climate map'],
      ['koppen'],
      ['köppen'],
      ['tropical climate'],
      ['arid climate'],
      ['desert climate'],
      ['temperate climate'],
      ['mediterranean climate'],
      ['oceanic climate'],
      ['continental climate'],
      ['polar climate'],
      ['tundra climate'],
      ['subarctic climate'],
      ['subtropical climate'],
      ['steppe climate'],
      ['tropical zone'],
      ['arid zone'],
      ['temperate zone'],
    ],
    buildIntent(text) {
      const classMatch = text.match(
        /\b(tropical|arid|desert|temperate|mediterranean|oceanic|continental|polar|tundra|subarctic|subtropical|steppe)\b/i
      )
      return {
        type: 'climate_zones',
        climate_class: classMatch ? classMatch[1].trim() : undefined,
        location: extractLocation(text),
        _fastQuery: true,
        _conceptMatch: 'climate_zones',
      }
    },
  },

  // ── Freedom House Index ──────────────────────────────────────────
  {
    id: 'freedom_house',
    opType: 'freedom_house',
    priority: 10,
    keywords: [
      ['freedom house'],
      ['political freedom'],
      ['freedom index'],
      ['civil liberty'],
      ['civil liberties'],
      ['freedom rating'],
      ['freedom score'],
      ['freedom map'],
      ['free countries'],
      ['not free'],
      ['partly free'],
      ['unfree'],
      ['authoritarian'],
    ],
    buildIntent(text) {
      const statusMatch = text.match(/\b(free|not\s+free|partly\s+free|unfree|authoritarian)\b/i)
      return {
        type: 'freedom_house',
        status_filter: statusMatch ? statusMatch[1].trim() : undefined,
        location: extractLocation(text),
        _fastQuery: true,
        _conceptMatch: 'freedom_house',
      }
    },
  },
]

// ── Matching engine ─────────────────────────────────────────────────

/**
 * Check if ALL keywords in a keyword group appear in the text.
 *
 * CL-144: keywords match on WORD BOUNDARIES (with an s/es plural tolerance),
 * not raw substrings. Substring matching let "purple monkey dishWASHer" match
 * the who_health 'wash' keyword and "TBilisi" match 'tb' — arbitrary text
 * could trigger a dataset. Word-anchored matching keeps "reef"→"reefs" and
 * "volcano"→"volcanoes" working while rejecting mid-word hits.
 *
 * @param {string} lowerText — lowercase user text
 * @param {string[]} keywordGroup — array of keywords that must ALL be present
 * @returns {boolean}
 */
const _keywordRegexCache = new Map()

function keywordRegex(kw) {
  let re = _keywordRegexCache.get(kw)
  if (!re) {
    const escaped = kw.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    re = new RegExp(`\\b${escaped}(?:s|es)?\\b`, 'i')
    _keywordRegexCache.set(kw, re)
  }
  return re
}

function matchesKeywordGroup(lowerText, keywordGroup) {
  return keywordGroup.every((kw) => keywordRegex(kw).test(lowerText))
}

/**
 * Try to match user text against the concept registry.
 * Returns the first matching concept's intent, or null if no match.
 *
 * @param {string} text — the user's raw text
 * @returns {object|null} — intent object with _fastQuery: true, or null
 */
// Spatial-analysis queries (suitability, similarity, proximity, center) must
// reach the Worker's typed tools — never a single-dataset concept match.
// A concept keyword ("arable land") inside a multi-criteria question is a
// lexical accident, not a dataset request. Observed live 2026-06-12:
// "Which African countries are most suitable for farming based on arable
// land and clean water?" → faostat concept match → worker never consulted.
// Keep in sync with the isEducationalQuestion exclusions in intent-parser.js
// and forcedToolFor() in worker/src/index.js.
const SPATIAL_ANALYSIS_GUARD =
  /\b(?:suitab\w*|most\s+(?:similar|alike)|most\s+like\b|twin\s+countr\w*|closest|nearest|within\s+\d+(?:\.\d+)?\s*(?:km|kilometers?|kilometres?|miles?|mi)\b|cent(?:er|re)\s+of|middle\s+of|most\s+central|median\s+cent\w*|mean\s+cent\w*|weighted\s+cent\w*|spread\s+out|moran|autocorrelat\w*|(?:spatially|geographically)\s+cluster\w*|nearest\s+neighbou?r|evenly\s+spac\w*|(?:hot|cold)\s*spots?|getis|deviational\s+ellipse|directional\s+trend|population\s+(?:change|growth|decline)|lisa|local\s+(?:moran|clusters?)|spatial\s+outliers?|surrounded\s+by|gravity\s+model|spatial\s+interaction|gravitational\s+pull|pull\s+(?:between|on)|geographically\s+weighted|\bgwr\b|typolog\w*|development\s+typ\w*|bivariate|versus|vs\.?|compare|correlat\w*|relationship\s+between|border(?:s|ing)?\b|neighbou?r\w*)\b/i

export function tryConceptMatch(text) {
  const lower = text.toLowerCase().trim()

  // Analytical queries bypass the concept registry entirely → AI path.
  if (SPATIAL_ANALYSIS_GUARD.test(lower)) return null

  // CL-144 guards — a dataset keyword inside these phrasings is a lexical
  // accident, not a dataset request:
  // 1. Numeric-threshold filters ("countries with life expectancy above 80")
  //    need the filter/SQL path, not a whole-dataset render.
  if (
    /\b(?:above|below|over|under|more\s+than|less\s+than|at\s+least|greater\s+than|fewer\s+than)\s+[\d]/.test(
      lower
    )
  )
    return null
  // 2. "Color the world/countries by X" is choropleth phrasing — the metric
  //    may not even exist in the keyword-matched dataset. Defer to the AI.
  if (/^colou?r\b/.test(lower) && /\bby\b/.test(lower)) return null
  // 3. "…by region/state/province" asks for an admin-level breakdown
  //    (division choropleth), not a country-level dataset render.
  if (/\bby\s+(?:region|state|province|district|county|division)s?\b/.test(lower)) return null

  // Sort concepts by priority (lower = first)
  const sorted = [...CONCEPTS].sort((a, b) => a.priority - b.priority)

  for (const concept of sorted) {
    // Check if any keyword group fully matches
    const matched = concept.keywords.some((group) => {
      // Handle single-keyword groups (string wrapped in array)
      const keywords = Array.isArray(group) ? group : [group]
      return matchesKeywordGroup(lower, keywords)
    })

    if (matched) {
      const intent = concept.buildIntent(text)
      if (intent) {
        console.log(
          `[ConceptRegistry] Matched concept "${concept.id}" for: "${text.slice(0, 60)}..."`
        )
        return intent
      }
    }
  }

  return null
}

/**
 * List all registered concept IDs (for debugging / inspection).
 * @returns {string[]}
 */
export function listConcepts() {
  return CONCEPTS.map((c) => c.id)
}

/**
 * Get a concept by ID (for testing / debugging).
 * @param {string} id
 * @returns {object|undefined}
 */
export function getConcept(id) {
  return CONCEPTS.find((c) => c.id === id)
}
